import React, {useState} from "react";
import {
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    Typography
} from "@mui/material";
import {post} from "../../api/api";
import {useAppStore} from "../../store/appStore";

const UserAgreement: React.FC = () => {
    const [checked, setChecked] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const onAgree = async () => {
        setIsSubmitting(true);

        try {
            const userModel = await post("/me/accept-agreement");
            useAppStore.setState({userModel});
        } catch (error) {
            console.warn("Failed to sign a user agreement: ", error);
        } finally {
            setIsSubmitting(false)
        }
    };
    return (
        <Dialog open={true} maxWidth="md" fullWidth>
            <DialogTitle>Algemene Voorwaarden voor het gebruik van Medendo</DialogTitle>
            <DialogContent dividers>
                <Typography variant="body1" component="p">
                    Laatst bijgewerkt: 10 februari 2025
                </Typography>
                <Typography variant="body1" component="p" mt='24px'>
                    Welkom bij Medendo! Medendo biedt een SaaS-applicatie waarmee zorgprofessionals efficiënter
                    kunnen werken met behulp van AI-ondersteuning. Door gebruik te maken van onze diensten, ga je
                    akkoord met de volgende voorwaarden.
                </Typography>

                <Typography variant="h6" mt='24px'>1. Algemeen</Typography>
                <Typography variant="body2" component="p">
                    Deze Algemene Voorwaarden (“Voorwaarden”) zijn van toepassing op het gebruik van de Medendo
                    SaaS-applicatie en bijbehorende diensten. Door een account aan te maken en Medendo te gebruiken,
                    verklaar je dat je deze Voorwaarden hebt gelezen, begrepen en ermee akkoord gaat.
                    Medendo behoudt zich het recht voor om deze Voorwaarden op elk moment te wijzigen. Wijzigingen
                    worden gecommuniceerd via de applicatie of per e-mail. Indien je niet akkoord gaat met de
                    gewijzigde voorwaarden, dien je het gebruik van Medendo te staken.
                </Typography>

                <Typography variant="h6" mt='24px'>2. Gebruik van de Medendo Assistent</Typography>
                <Typography variant="body2" component="p">
                    Medendo biedt een AI-assistent die spraak kan transcriberen en verwerken. Bij gebruik van deze
                    assistent gelden de volgende regels:
                </Typography>
                <Typography variant="body2" component="p">
                    • Informatie en toestemming: Je bent verplicht om je cliënt of patiënt te informeren over het
                    gebruik van de Medendo Assistent, inclusief het opnemen van audio en de verwerking van
                    informatie. Daarnaast dien je expliciete toestemming te verkrijgen voordat je de Medendo
                    Assistent gebruikt.
                </Typography>
                <Typography variant="body2" component="p">
                    • Controle en verantwoordelijkheid: De output van de Medendo Assistent moet door jou als
                    gebruiker worden gecontroleerd op onjuistheden of onvolledigheden. Jij bent zelf
                    verantwoordelijk voor de juistheid van de transcriptie en rapportage.
                </Typography>
                <Typography variant="body2" component="p">
                    • Beperkt gebruik: Het is niet toegestaan om de AI-assistent te gebruiken voor andere doeleinden
                    dan waarvoor deze bedoeld is. De Medendo Assistent mag alleen worden ingezet binnen een
                    professionele zorgcontext.
                </Typography>

                <Typography variant="h6" mt='24px'>3. Verantwoordelijkheden van de Gebruiker</Typography>
                <Typography variant="body2" component="p">
                    • Beveiliging van je account:
                </Typography>
                <Typography variant="body2" component="p">
                    • Je dient Multi-Factor Authenticatie (MFA) te gebruiken en je account veilig te houden conform
                    het beleid van je werkgever.
                </Typography>
                <Typography variant="body2" component="p">
                    • Je bent verantwoordelijk voor het geheimhouden van je inloggegevens en voor alle activiteiten
                    die onder jouw account plaatsvinden.
                </Typography>

                <Typography variant="h6" mt='24px'>4. Intellectuele Eigendom</Typography>
                <Typography variant="body2" component="p">
                    • Alle rechten, titels en belangen met betrekking tot Medendo en de onderliggende technologieën,
                    inclusief maar niet beperkt tot software, algoritmes en gebruikersinterfaces, zijn eigendom van
                    Medendo.
                </Typography>
                <Typography variant="body2" component="p">
                    • Het is niet toegestaan om de software, de AI-modellen of andere intellectuele eigendommen van
                    Medendo te kopiëren, distribueren, wijzigen, of reverse-engineeren zonder uitdrukkelijke
                    schriftelijke toestemming.
                </Typography>

                <Typography variant="h6" mt='24px'>5. Beperkingen van aansprakelijkheid</Typography>
                <Typography variant="body2" component="p">
                    • Medendo streeft naar een hoge mate van nauwkeurigheid en betrouwbaarheid, maar garandeert niet
                    dat alle gegenereerde transcripties en rapportages foutloos zijn.
                </Typography>
                <Typography variant="body2" component="p">
                    • Medendo is niet aansprakelijk voor schade als gevolg van onjuiste, onvolledige of verkeerd
                    geïnterpreteerde informatie, noch voor beslissingen die gebaseerd zijn op de AI-output.
                </Typography>

                <Typography variant="h6" mt='24px'>6. Privacy en Gegevensbescherming</Typography>
                <Typography variant="body2" component="p">
                    • Medendo verwerkt persoonsgegevens volgens de geldende privacywetgeving, waaronder de AVG. In
                    onze Privacyverklaring wordt uitgelegd hoe wij gegevens verwerken en beveiligen.
                </Typography>

                <Typography variant="h6" mt='24px'>7. Opschorting en Beëindiging van de Dienst</Typography>
                <Typography variant="body2" component="p">
                    • Medendo behoudt zich het recht voor om je toegang tot de dienst tijdelijk of permanent op te
                    schorten bij misbruik, schending van deze voorwaarden of beveiligingsrisico’s.
                </Typography>

                <Typography variant="h6" mt='24px'>8. Toepasselijk Recht en Geschillen</Typography>
                <Typography variant="body2" component="p">
                    • Op deze Voorwaarden is het Nederlands recht van toepassing.
                </Typography>
                <Typography variant="body2" component="p">
                    • Geschillen die voortvloeien uit of verband houden met het gebruik van Medendo worden
                    voorgelegd aan de bevoegde rechter in Nederland.
                </Typography>

                <Typography variant="h6" mt='24px'>9. Contactgegevens</Typography>
                <Typography variant="body2" component="p">
                    Voor vragen over deze Voorwaarden kun je contact opnemen met Medendo via <a
                    href="mailto:info@medendo.com">info@medendo.com</a>.
                </Typography>

                <Typography variant="body1" component="p" mt='24px'>
                    Door Medendo te gebruiken, ga je akkoord met deze Algemene Voorwaarden.
                </Typography>
            </DialogContent>
            <DialogActions>
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={checked}
                            onChange={(e) => setChecked(e.target.checked)}
                            name="accept"
                        />
                    }
                    label="Ik ga akkoord met de Algemene Voorwaarden"
                />
                <Button onClick={onAgree} disabled={!checked || isSubmitting} variant="contained" color="primary">
                    Akkoord
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default UserAgreement;
